import { useState, useEffect, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { useAuth } from '../../../AuthContext';
import useAuditLog, { AUDIT_ACTION } from '../../../hooks/useAuditLog';
import { fetchData } from '../../../apiService';
import LogoLoader from '../../common/LogoLoader';
import { useSessionValidation } from '../../../utils/sessionValidation';
import { Refresh, GetApp, ArrowBack } from '@mui/icons-material';
import { IconButton, Tooltip, Alert, Snackbar } from '@mui/material';
import { exportStyledExcel } from '../../../utils/excelExport';

const PRIMARY     = '#1c2266';
const PRIMARY_MID = '#2d35a0';
const PRIMARY_LT  = '#eef0ff';

const fmtQ = (n) => (parseFloat(n) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtV = (n) => `₹${(parseFloat(n) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmt2Date = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
};

const iconBtn = {
    border: '1px solid rgba(255,255,255,0.3)', borderRadius: '8px',
    height: 29, width: 29, color: '#fff', flexShrink: 0,
    '&:hover': { background: 'rgba(255,255,255,0.1)' },
    '&.Mui-disabled': { color: 'rgba(255,255,255,0.3)' },
};

const HSNTaxSummary = () => {
    useSessionValidation();
    const { companyId, branchId, token, companyName, username } = useAuth();
    const { logAudit } = useAuditLog();
    const history = useHistory();

    const today = new Date();
    const [fromDate, setFromDate] = useState(fmt2Date(new Date(today.getFullYear(), today.getMonth(), 1)));
    const [toDate, setToDate]     = useState(fmt2Date(today));
    const [data, setData]         = useState([]);
    const [loading, setLoading]   = useState(false);
    const [error, setError]       = useState('');
    const [fetched, setFetched]   = useState(false);
    const [search, setSearch]     = useState('');

    useEffect(() => {
        if (companyId && branchId && token && fromDate && toDate) {
      if (new Date(fromDate) > new Date(toDate)) {
        setData([]);
        setFetched(false);
        setError('From Date cannot be greater than To Date');
        return;
      }
      fetchReport();
    }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [companyId, branchId, token]);

    const fetchReport = async () => {
        if (!fromDate || !toDate) { setError('Please select date range'); return; }
        if (new Date(fromDate) > new Date(toDate)) { setError('From Date cannot be greater than To Date'); return; }
        setLoading(true);
        try {
            const res = await fetchData(
                'GSTReportApi',
                `/HSNAndTaxSummaryDetails/${companyId}/${branchId}/${fromDate}/${toDate}`,
                token
            );
            const rows = res?.response_data || res || [];
            setData(Array.isArray(rows) ? rows : []);
            setFetched(true);
        } catch (err) {
            console.error(err);
            setError('Failed to fetch HSN tax summary');
        } finally {
            setLoading(false);
        }
    };

    const filtered = useMemo(() => {
        if (!search.trim()) return data;
        const q = search.toLowerCase();
        return data.filter(r =>
            (r.HSNCode || '').toLowerCase().includes(q) ||
            String(r.GSTRate || '').includes(q)
        );
    }, [data, search]);

    const totals = useMemo(() => filtered.reduce((acc, r) => ({
        TotalQty:     acc.TotalQty     + (parseFloat(r.TotalQty)     || 0),
        TaxableValue: acc.TaxableValue + (parseFloat(r.TaxableValue) || 0),
        CGST:         acc.CGST         + (parseFloat(r.CGST)         || 0),
        SGST:         acc.SGST         + (parseFloat(r.SGST)         || 0),
        IGST:         acc.IGST         + (parseFloat(r.IGST)         || 0),
        TotalValue:   acc.TotalValue   + (parseFloat(r.TotalValue)   || 0),
    }), { TotalQty: 0, TaxableValue: 0, CGST: 0, SGST: 0, IGST: 0, TotalValue: 0 }), [filtered]);

    const handleExport = async () => {
        logAudit(AUDIT_ACTION.EXPORT, 'HSNTaxSummary', 0);
        if (!filtered.length) return;
        const headers = ['#', 'HSN Code', 'GST Rate %', 'Total Qty', 'Taxable Value', 'CGST', 'SGST', 'IGST', 'Total Value'];
        const rows = filtered.map((r, i) => [
            i + 1, r.HSNCode, r.GSTRate ? `${r.GSTRate}%` : '-',
            r.TotalQty, r.TaxableValue, r.CGST, r.SGST, r.IGST, r.TotalValue,
        ]);

        await exportStyledExcel({
            reportTitle: 'HSN-TAX WISE SUMMARY REPORT',
            sheetName: 'HSN Tax Summary',
            fileName: `HSNTaxSummary_${fromDate}_${toDate}.xlsx`,
            companyName,
            username,
            fromDate,
            toDate,
            headers: headers,
            rows,
            totalsRow: ['', '', 'Totals',
                fmtQ(totals.TotalQty), fmtV(totals.TaxableValue),
                fmtV(totals.CGST), fmtV(totals.SGST), fmtV(totals.IGST), fmtV(totals.TotalValue)],
            columnWidths: [4, 14, 12, 12, 16, 14, 14, 14, 16],
        });
    };

    const thBase = { padding: '6px 8px', fontWeight: 700, fontSize: 11, whiteSpace: 'nowrap', border: `1px solid ${PRIMARY_MID}`, color: '#fff', background: PRIMARY_MID };
    const tdBase = { padding: '5px 8px', fontSize: 11, whiteSpace: 'nowrap', border: '1px solid #e2e8f0' };

    return (
        <div style={{ minHeight: '100%', background: '#f0f2f5', padding: 12, fontFamily: "'Inter', sans-serif", boxSizing: 'border-box' }}>
            <LogoLoader open={loading} message="Loading HSN tax summary..." />
            <Snackbar open={!!error} autoHideDuration={5000} onClose={() => setError('')} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
                <Alert severity="error" onClose={() => setError('')} sx={{ width: '100%' }}>{error}</Alert>
            </Snackbar>

            <div style={{ background: '#fff', borderRadius: 10, boxShadow: '0 4px 12px rgba(0,0,0,0.08)', border: '1px solid #e2e8f0', overflow: 'hidden' }}>

                {/* Toolbar */}
                <div className="report-toolbar" style={{ display: 'flex', alignItems: 'center', padding: '6px 12px', background: `linear-gradient(135deg, ${PRIMARY} 0%, ${PRIMARY_MID} 100%)`, gap: 6, flexWrap: 'nowrap', overflowX: 'auto', overflowY: 'hidden', scrollbarWidth: 'none' }}>
                    <Tooltip title="Back" arrow>
                        <IconButton onClick={() => history.push('/ReportsHub')} size="small" sx={iconBtn}>
                            <ArrowBack sx={{ fontSize: 16 }} />
                        </IconButton>
                    </Tooltip>
                    <span style={{ fontSize: 13, fontWeight: 700, color: '#fff', whiteSpace: 'nowrap', flexShrink: 0 }}>
                        HSN-Tax Wise Summary
                        <span style={{ fontSize: 11, fontWeight: 400, opacity: 0.8, display: 'block', lineHeight: 1.2 }}>GST Report</span>
                    </span>
                    <div style={{ flex: 1, minWidth: 4 }} />
                    <input placeholder="Search HSN / GST rate..."
                        value={search} onChange={e => setSearch(e.target.value)}
                        style={{ height: 29, border: '1px solid rgba(255,255,255,0.3)', borderRadius: 8, padding: '0 10px', fontSize: 12, backgroundColor: 'rgba(255,255,255,0.1)', color: '#fff', colorScheme: 'dark', flexShrink: 0, minWidth: 180, outline: 'none' }} />
                    <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)}
                        style={{ height: 29, border: '1px solid rgba(255,255,255,0.3)', borderRadius: 8, padding: '0 8px', fontSize: 12, backgroundColor: 'rgba(255,255,255,0.1)', color: '#fff', colorScheme: 'dark', flexShrink: 0 }} />
                    <input type="date" value={toDate} onChange={e => setToDate(e.target.value)}
                        style={{ height: 29, border: '1px solid rgba(255,255,255,0.3)', borderRadius: 8, padding: '0 8px', fontSize: 12, backgroundColor: 'rgba(255,255,255,0.1)', color: '#fff', colorScheme: 'dark', flexShrink: 0 }} />
                    <Tooltip title="Refresh" arrow>
                        <IconButton onClick={fetchReport} disabled={loading} size="small" sx={iconBtn}>
                            <Refresh sx={{ fontSize: 16 }} />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Export Excel" arrow>
                        <IconButton onClick={handleExport} disabled={!filtered.length} size="small" sx={iconBtn}>
                            <GetApp sx={{ fontSize: 16 }} />
                        </IconButton>
                    </Tooltip>
                </div>

                {/* Summary bar */}
                {fetched && filtered.length > 0 && (
                    <div style={{ display: 'flex', gap: 8, padding: '6px 14px', borderBottom: '1px solid #f1f5f9', flexWrap: 'wrap' }}>
                        {[
                            { label: 'HSN Codes',     value: filtered.length,               color: PRIMARY },
                            { label: 'Total Qty',     value: fmtQ(totals.TotalQty),          color: '#374151' },
                            { label: 'Taxable Value', value: fmtV(totals.TaxableValue),      color: '#374151' },
                            { label: 'CGST',          value: fmtV(totals.CGST),              color: '#0277bd' },
                            { label: 'SGST',          value: fmtV(totals.SGST),              color: '#0277bd' },
                            { label: 'IGST',          value: fmtV(totals.IGST),              color: '#7c3aed' },
                            { label: 'Total Value',   value: fmtV(totals.TotalValue),        color: '#059669' },
                        ].map(s => (
                            <div key={s.label} style={{ background: PRIMARY_LT, borderRadius: 8, padding: '4px 12px', display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 90 }}>
                                <span style={{ fontSize: 10, color: '#64748b', fontWeight: 600 }}>{s.label}</span>
                                <span style={{ fontSize: 12, color: s.color, fontWeight: 700 }}>{s.value}</span>
                            </div>
                        ))}
                    </div>
                )}

                {/* Meta */}
                {fetched && (
                    <div style={{ padding: '4px 14px', fontSize: 11, color: '#64748b', borderBottom: '1px solid #f1f5f9' }}>
                        {fromDate} to {toDate} &nbsp;|&nbsp; {filtered.length} HSN codes
                        {search && ` (filtered from ${data.length})`}
                    </div>
                )}

                {/* Table */}
                <div className="report-table-scroll" style={{ overflow: 'auto', overflowY: 'scroll', maxHeight: 'calc(100vh - 190px)' }}>
                    <table style={{ borderCollapse: 'collapse', fontSize: 11, minWidth: 750, width: '100%' }}>
                        <thead style={{ position: 'sticky', top: 0, zIndex: 3 }}>
                            <tr>
                                <th style={{ ...thBase, textAlign: 'center', width: 36 }}>#</th>
                                <th style={{ ...thBase, textAlign: 'left', minWidth: 120 }}>HSN Code</th>
                                <th style={{ ...thBase, textAlign: 'right', minWidth: 90 }}>GST Rate %</th>
                                <th style={{ ...thBase, textAlign: 'right', minWidth: 100 }}>Total Qty</th>
                                <th style={{ ...thBase, textAlign: 'right', minWidth: 130 }}>Taxable Value</th>
                                <th style={{ ...thBase, textAlign: 'right', minWidth: 110 }}>CGST</th>
                                <th style={{ ...thBase, textAlign: 'right', minWidth: 110 }}>SGST</th>
                                <th style={{ ...thBase, textAlign: 'right', minWidth: 110 }}>IGST</th>
                                <th style={{ ...thBase, textAlign: 'right', minWidth: 130, background: '#1a1f6e' }}>Total Value</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.length === 0 ? (
                                <tr>
                                    <td colSpan={9} style={{ textAlign: 'center', padding: 32, color: '#94a3b8', fontSize: 13 }}>
                                        {fetched ? 'No records found' : 'Select date range and click Refresh'}
                                    </td>
                                </tr>
                            ) : filtered.map((r, i) => (
                                <tr key={`${r.HSNCode}-${i}`} style={{ background: i % 2 === 0 ? '#fff' : PRIMARY_LT }}>
                                    <td style={{ ...tdBase, textAlign: 'center', color: '#94a3b8' }}>{i + 1}</td>
                                    <td style={{ ...tdBase, fontWeight: 700, color: PRIMARY, letterSpacing: '0.5px' }}>{r.HSNCode || '-'}</td>
                                    <td style={{ ...tdBase, textAlign: 'right' }}>
                                        {r.GSTRate ? (
                                            <span style={{ padding: '2px 10px', borderRadius: 8, fontSize: 10, fontWeight: 700, background: '#fef3c7', color: '#d97706' }}>
                                                {r.GSTRate}%
                                            </span>
                                        ) : '-'}
                                    </td>
                                    <td style={{ ...tdBase, textAlign: 'right', color: '#374151', fontWeight: 600 }}>{fmtQ(r.TotalQty)}</td>
                                    <td style={{ ...tdBase, textAlign: 'right', color: '#374151', fontWeight: 600 }}>{fmtV(r.TaxableValue)}</td>
                                    <td style={{ ...tdBase, textAlign: 'right', color: '#0277bd' }}>{parseFloat(r.CGST) ? fmtV(r.CGST) : '-'}</td>
                                    <td style={{ ...tdBase, textAlign: 'right', color: '#0277bd' }}>{parseFloat(r.SGST) ? fmtV(r.SGST) : '-'}</td>
                                    <td style={{ ...tdBase, textAlign: 'right', color: '#7c3aed' }}>{parseFloat(r.IGST) ? fmtV(r.IGST) : '-'}</td>
                                    <td style={{ ...tdBase, textAlign: 'right', fontWeight: 700, color: '#059669' }}>{fmtV(r.TotalValue)}</td>
                                </tr>
                            ))}
                        </tbody>
                        {filtered.length > 0 && (
                            <tfoot>
                                <tr style={{ background: PRIMARY_LT, fontWeight: 700, position: 'sticky', bottom: 0 }}>
                                    <td colSpan={3} style={{ ...tdBase, fontWeight: 700, color: PRIMARY, textAlign: 'right' }}>Totals</td>
                                    <td style={{ ...tdBase, textAlign: 'right', fontWeight: 700, color: '#374151' }}>{fmtQ(totals.TotalQty)}</td>
                                    <td style={{ ...tdBase, textAlign: 'right', fontWeight: 700, color: '#374151' }}>{fmtV(totals.TaxableValue)}</td>
                                    <td style={{ ...tdBase, textAlign: 'right', fontWeight: 700, color: '#0277bd' }}>{fmtV(totals.CGST)}</td>
                                    <td style={{ ...tdBase, textAlign: 'right', fontWeight: 700, color: '#0277bd' }}>{fmtV(totals.SGST)}</td>
                                    <td style={{ ...tdBase, textAlign: 'right', fontWeight: 700, color: '#7c3aed' }}>{fmtV(totals.IGST)}</td>
                                    <td style={{ ...tdBase, textAlign: 'right', fontWeight: 700, color: '#059669' }}>{fmtV(totals.TotalValue)}</td>
                                </tr>
                            </tfoot>
                        )}
                    </table>
                </div>
            </div>
        </div>
    );
};

export default HSNTaxSummary;
